/* eslint-disable no-console */
import * as Sentry from '@sentry/react';
import useSWR, { KeyedMutator } from 'swr';
import isNonProduction from 'utilities/isNonProduction';
import AddressWithFirstAndLastName from '../types/AddressWithFirstAndLastName';
import FinalizeOrderResponse from '../types/FinalizeOrderResponse';
import Invoice from '../types/Invoice';
import { PaymentResponse } from '../types/PaymentResponse';
import uuid from '../utilities/uuid';

export const baseUrl = process.env.REACT_APP_SHOPPING_API_BASE_URL as string;
export const apiVersion = 'v7.1';

/**
 * Structured error type for API fetch failures
 * Used by SWR to provide detailed error information
 */
export interface FetchError extends Error {
  statusCode?: number;
  info?: {
    message: string;
  };
}

async function fetcher<TData>(
  input: RequestInfo,
  init?: RequestInit
): Promise<TData> {
  const res = await fetch(input, init);

  if (!res.ok) {
    const error = new Error(
      'An error occurred while fetching the data.'
    ) as FetchError;

    // Clone the response before reading it for the error
    try {
      error.info = await res.clone().json();
    } catch (e) {
      error.info = { message: 'Failed to parse error response' };
    }

    error.statusCode = res.status;

    const level = res.status === 500 ? 'warning' : 'error';
    Sentry.captureMessage(`${level} - ${error.statusCode}: SWR fetcher`, level);

    throw error;
  }

  return res.json();
}

export function useGetInvoice(invoiceId: string): {
  invoice: Invoice | undefined;
  isLoading: boolean;
  isError: FetchError | undefined;
  mutateInvoice: KeyedMutator<Invoice>;
} {
  const { data, error, mutate } = useSWR<Invoice, FetchError>(
    `${baseUrl}/${apiVersion}/invoices/${invoiceId}`,
    fetcher
  );

  return {
    invoice: data,
    isLoading: !error && !data,
    isError: error,
    mutateInvoice: mutate,
  };
}

export async function payInvoice(
  invoiceId: string,
  paymentToken: string,
  billingAddress: AddressWithFirstAndLastName | null
): Promise<PaymentResponse> {
  const response = await fetch(
    `${baseUrl}/${apiVersion}/invoices/${invoiceId}/pay`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        paymentToken,
        overrideAmount: null,
        billingAddress,
        requestId: uuid(),
        utcOffsetInMinutes: -new Date().getTimezoneOffset(),
      }),
    }
  );

  if (!response.ok) {
    const level = response.status === 500 ? 'warning' : 'error';

    Sentry.captureMessage(
      `${level} - ${response.status}: payInvoice - ${response.statusText}`,
      level
    );
  }

  return response.json();
}

export async function finalizeOrder(
  invoiceId: string,
  customerPhoneNumber: string,
  shippingAddress: AddressWithFirstAndLastName | null,
  billingAddress: AddressWithFirstAndLastName | null
): Promise<FinalizeOrderResponse> {
  // The time-zone offset is the difference, in minutes, from local time to UTC.
  // Note that this means that the offset is positive if the local timezone is behind UTC,
  // and negative if it is ahead.
  const utcOffsetInMinutes = -new Date().getTimezoneOffset();

  const payload = {
    customerPhoneNumber,
    shippingAddress,
    billingAddress,
    utcOffsetInMinutes,
  };

  if (isNonProduction) {
    console.log(
      `finalizeOrder payload from ShoppingApi: ${JSON.stringify(
        payload,
        null,
        2
      )}`
    );
  }

  const response = await fetch(
    `${baseUrl}/${apiVersion}/invoices/${invoiceId}/finalize`,
    {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  const responseBody = await response.json();

  if (!response.ok) {
    const level = response.status === 500 ? 'warning' : 'error';

    Sentry.captureMessage(
      `${level} - ${response.status}: finalizeOrder - ${JSON.stringify(
        responseBody
      )}`,
      level
    );
  }

  return responseBody;
}

const ShoppingApi = { useGetInvoice, payInvoice, finalizeOrder };

export default ShoppingApi;
